import {FC, useEffect, useRef, useState} from 'react';
import {toast} from 'react-toastify';
//mui
import {Box, useMediaQuery} from '@mui/material';
//store
import {getCurrentEventSelector, getEventTodoList, getEventsSelector} from '@/store/organization-event';
import {clearDashboardData, getDashboardStatisticsSelector} from '@/store/organization-dashboard';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
//icons
import {ReactComponent as EmptyCalendarIcon} from '@icons/calendar-empty.svg';
//components
import TodoList from '@/components/Organization/Dashboard/TodoList';
import Statistics from '@/components/Organization/Dashboard/Statistics';
import Modal from '@/shared/components/Modal';
import CreateNewEvent, {CreateNewEventHandle} from '@/components/Organization/Dashboard/CreateNewEvent';
import EmptyContent from '@/shared/components/Table/EmptyContent';
import Loader from '@/shared/components/Loader';
//constants
import {CREATE_FIRST_EVENT, CREATE_NEW_EVENT} from '@/utils/constants';
//helpers
import {getErrorMessage} from '@/utils/helpers';
//styles
import {dashboardStyle} from './Dashboard.styles';
import MobDashboard from './MobDashboard';
import AttentionList from '@/components/Organization/Dashboard/AttentionList.tsx';

const Dashboard: FC = () => {
  const dispatch = useAppDispatch();

  const [isShowNewEventModal, setIsShowNewEventModal] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const createEventRef = useRef<CreateNewEventHandle>(null);

  const handleModalChange = () => setIsShowNewEventModal(!isShowNewEventModal);
  const handleModalCloseAttempt = () => {
    if (createEventRef.current) {
      createEventRef.current.requestClose();
    } else {
      handleModalChange();
    }
  };

  const events = useAppSelector(getEventsSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const statistics = useAppSelector(getDashboardStatisticsSelector);

  useEffect(() => {
    const fetchDashboardData = async () => {
      dispatch(clearDashboardData());
      setIsDataLoaded(false);

      try {
        const reqBody = {id: currentEvent!.id, isDone: false};
        await Promise.all([dispatch(getEventTodoList(reqBody)).unwrap()]);
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setIsDataLoaded(true);
      }
    };

    if (currentEvent?.id) {
      fetchDashboardData();
    } else if (events && !events.length) {
      setIsDataLoaded(true);
    }
  }, [currentEvent?.id, events?.length]);

  const isLaptop = useMediaQuery('(min-width:1200px)');

  return (
    <Box sx={dashboardStyle.wrapper}>
      {isLaptop ? (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
          {isDataLoaded ? (
            !!events?.length && statistics ? (
              <Box sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                <Statistics />
                <Box sx={dashboardStyle.contentWrapper}>
                  <Box sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                    <TodoList />
                  </Box>
                  <Box sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                    <AttentionList />
                  </Box>
                </Box>
              </Box>
            ) : (
              <EmptyContent
                title="There are no events created yet. Create your first event."
                icon={EmptyCalendarIcon}
                buttonTitle={CREATE_FIRST_EVENT}
                onModalChange={handleModalChange}
                parentClassName={dashboardStyle.emptyContent}
              />
            )
          ) : (
            <Loader parentClassName={dashboardStyle.loader} size={50} />
          )}
          <Modal
            onClose={handleModalCloseAttempt}
            open={isShowNewEventModal}
            sx={dashboardStyle.modal}
            title={CREATE_NEW_EVENT}
          >
            <CreateNewEvent onCloseModal={handleModalChange}  ref={createEventRef} />
          </Modal>
        </Box>
      ) : (
        <MobDashboard />
      )}
    </Box>
  );
};

export default Dashboard;
